import { useTranslation } from "react-i18next";
import { ThumbsDown, ThumbsUp } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../../atoms/Button";

export type AIFeedback = "positive" | "negative";

export interface AIFeedbackButtonsProps {
  onFeedback?: (feedback: AIFeedback) => void;
  /**
   * Which thumb is filled. Fully consumer-controlled -- clicking reports through `onFeedback` but
   * does not fill on its own, so the rating survives wherever the consumer stores it.
   */
  value?: AIFeedback;
  /** @default "sm" */
  size?: "sm" | "xs";
  className?: string;
}

/**
 * A thumbs up/down row for rating an AI response. Shared by `SimpleChatMessage` (composed into its
 * `.Footer`) and `AIChatConfirmationMessage` (as its `.Feedback` part) so the affordance cannot
 * drift between them.
 *
 * Carries no margin of its own -- spacing belongs to the layout placing it. Icon sizing is the
 * `Button`'s to decide, driven by `size`, so the icons carry no dimensions of their own either.
 */
export const AIFeedbackButtons = ({ onFeedback, value, size = "sm", className }: AIFeedbackButtonsProps) => {
  const { t } = useTranslation();

  return (
    <div data-slot="ai-feedback-buttons" className={cn("flex items-center gap-1", className)}>
      <Button
        variant="ghost"
        color="muted"
        size={size}
        iconOnly
        onClick={() => onFeedback?.("positive")}
        hoverLabel={t("Good response")}
        aria-label={t("Good response")}
      >
        <ThumbsUp className={value === "positive" ? "fill-success text-success" : undefined} />
      </Button>
      <Button
        variant="ghost"
        color="muted"
        size={size}
        iconOnly
        onClick={() => onFeedback?.("negative")}
        hoverLabel={t("Poor response")}
        aria-label={t("Poor response")}
      >
        <ThumbsDown className={value === "negative" ? "fill-destructive text-destructive" : undefined} />
      </Button>
    </div>
  );
};
